import { useMemo, useState } from "react";
import moment from "moment";
import { makeStyles } from "tss-react/mui";
import {
  MiActions,
  MiAlert,
  MiCheckbox,
  MiDatePicker,
  MiModal,
  MiStepper,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  DATEPICKER_ACTIONS,
  DATEPICKER_VARIANTS,
  ICONS_WEB,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { toastService } from "@miplatform/toast";
import { InvoiceStatus } from "@miplatform/types";
import { getISOString } from "@miplatform/utils";
import * as mdi from "@mdi/js";
import { Box, Typography } from "@mui/material";
import { RECONCILIATION_STEPS } from "../../accountingConstants";
import { useReconcileContext } from "../ReconcileContext";

export const ReconcileFooter = () => {
  const { classes, theme } = useStyles();
  const {
    activeStep,
    setActiveStep,
    order,
    invoices,
    purchaseOrders,
    isInvoiceMode,
    isLoading,
    invoicesModule,
    isEverythingVerified,
    verificationState,
    setInvoicePdfs,
    invoiceFormsState,
  } = useReconcileContext();

  const hasExcessBalance = useMemo(() => {
    return purchaseOrders
      .filter(
        (po) => po.orderTemplateId === order.orderTemplateId && !po.voidDate
      )
      .some((po) => po.openBalance > 0);
  }, [purchaseOrders]);

  const quickBooksSyncedInvoices = invoices.filter(
    (invoice) => !invoice?.quickBooksAuditResponse
  );

  const [isInvoiceDateModalOpen, setIsInvoiceDateModalOpen] = useState(false);
  const [invoiceDate, setInvoiceDate] = useState(moment());
  const [isConfirmVarianceModalOpen, setIsConfirmVarianceModalOpen] =
    useState(false);

  const [
    invoiceIdsToExcludeFromQuickBooks,
    setInvoiceIdsToExcludeFromQuickBooks,
  ] = useState([]);
  const anyInvoiceExcluded = useMemo(() => {
    return invoiceIdsToExcludeFromQuickBooks.length > 0;
  }, [invoiceIdsToExcludeFromQuickBooks]);
  const { navigate } = useRouter();

  const steps = [
    { label: "Pre-Bill", value: RECONCILIATION_STEPS.RECONCILIATION },
    { label: "Review & Submit", value: RECONCILIATION_STEPS.REVIEW_SUBMIT },
  ];

  const hasInvalidForms = useMemo(() => {
    return Object.values(invoiceFormsState).some((f) => {
      // For variance items, only check if they're dirty (modified) and invalid
      if (f?.isVarianceItem) {
        return f?.isDirty && !f?.isValid;
      }
      // For regular items, check validity regardless of dirty state
      return !f?.isValid;
    });
  }, [invoiceFormsState]);

  const isAnyEditing = useMemo(() => {
    return Object.values(invoiceFormsState).some((f) => f?.isEditing);
  }, [invoiceFormsState]);

  const invoiceRequiresPaymentCommitment = (invoice) => {
    return invoice?.invoiceItems?.some((item) => item.unitPrice !== 0);
  };

  const isNextBtnDisabled = useMemo(() => {
    // Check that all invoices have at least one invoice item and one payment item
    const hasInvoiceItem = invoices.every(
      (invoice) => invoice?.invoiceItems?.length > 0
    );

    const hasRequiredPaymentItems = invoices.every((invoice) => {
      if (!invoiceRequiresPaymentCommitment(invoice)) {
        return true;
      }
      return invoice?.invoiceBillingCommitments?.length > 0;
    });

    if (activeStep === RECONCILIATION_STEPS.RECONCILIATION) {
      return (
        !hasInvoiceItem ||
        !hasRequiredPaymentItems ||
        hasInvalidForms ||
        isAnyEditing
      );
    }
    return false;
  }, [
    invoices,
    purchaseOrders,
    activeStep,
    order,
    hasInvalidForms,
    isAnyEditing,
  ]);

  const hasAnyInvoiceVariance = useMemo(() => {
    return invoices.some((invoice) => invoice.variance !== 0);
  }, [invoices]);

  const isInvoiceBtnDisabled = useMemo(() => {
    const hasInvoiceItem = invoices.every(
      (invoice) => invoice?.invoiceItems?.length > 0
    );
    const draftInvoicesMissingRequiredPayments = invoices
      .filter((inv) => inv.status === InvoiceStatus.Draft)
      .some(
        (invoice) =>
          invoiceRequiresPaymentCommitment(invoice) &&
          !invoice?.invoiceBillingCommitments?.length
      );

    const allVerified = isEverythingVerified();

    return (
      !hasInvoiceItem || draftInvoicesMissingRequiredPayments || !allVerified
    );
  }, [invoices, isEverythingVerified, verificationState]);

  const isInvoiceIncludedInQuickBooks = (id) =>
    !invoiceIdsToExcludeFromQuickBooks.includes(id);

  function toggleInvoiceExclusion(invoiceId, shouldInclude) {
    if (shouldInclude) {
      setInvoiceIdsToExcludeFromQuickBooks((prev) =>
        prev.filter((i) => i !== invoiceId)
      );
    } else {
      setInvoiceIdsToExcludeFromQuickBooks((prev) => [...prev, invoiceId]);
    }
  }

  const actions = [
    {
      name: "Back",
      variant: BUTTON_VARIANTS.OUTLINED,
      disabled: activeStep === 0,
      width: 100,
      onClick: () => {
        if (activeStep > 0) {
          setActiveStep(activeStep - 1);
          setInvoicePdfs([]);
        }
      },
    },
    {
      name: activeStep === steps.length - 1 ? "Complete" : "Next",
      variant: BUTTON_VARIANTS.CONTAINED,
      color: BUTTON_COLORS.PRIMARY,
      disabled: isNextBtnDisabled || isLoading,
      width: 100,
      onClick: () => {
        if (hasExcessBalance) {
          toastService.error({
            text: "Cannot proceed: There are payment information with excess balance. Please resolve them before continuing.",
          });
          return;
        }
        if (activeStep < steps.length - 1) {
          setActiveStep(activeStep + 1);
          invoicesModule.getOrderInvoicePdfs();
          return;
        }
        if (hasAnyInvoiceVariance) {
          setIsConfirmVarianceModalOpen(true);

          return;
        }
        invoicesModule.handlePreBillOrder();
      },
    },
  ];

  const invoicingActions = [
    {
      name: "Cancel",
      variant: BUTTON_VARIANTS.TEXT,
      width: 100,
      color: BUTTON_COLORS.BLACK,
      onClick: () => navigate("/accounting/invoices/pending-invoices"),
    },
    {
      name: "Verify & Invoice",
      variant: BUTTON_VARIANTS.CONTAINED,
      color: BUTTON_COLORS.PRIMARY,
      disabled: isInvoiceBtnDisabled || isLoading,
      width: 100,
      onClick: () => setIsInvoiceDateModalOpen(true),
    },
  ];

  return (
    <Box className={classes.root} justifyContent="flex-end">
      {!isInvoiceMode && (
        <MiStepper
          activeStep={activeStep}
          steps={steps}
          setActiveStep={setActiveStep}
        />
      )}
      <MiModal
        open={isInvoiceDateModalOpen}
        disableClick={isLoading}
        title="Verify & Sync Invoices"
        loading={isLoading}
        titleIcon={mdi[ICONS_WEB.CALENDAR]}
        leftIconColor={theme.palette.primary.main}
        width={320}
        closeModal={() => setIsInvoiceDateModalOpen(false)}
        description="Enter the date in which the invoice will be sent to the builder"
        actions={[
          {
            name: "Cancel",
            variant: BUTTON_VARIANTS.TEXT,
            size: "medium",
            color: BUTTON_COLORS.BLACK,
            onClick: () => setIsInvoiceDateModalOpen(false),
          },
          {
            name: "Invoice Order",
            variant: BUTTON_VARIANTS.CONTAINED,
            size: "medium",
            disabled: isLoading,
            onClick: async () => {
              await invoicesModule.handleFinalizeInvoice({
                invoiceDate: getISOString(invoiceDate),
                invoiceIdsToExcludeFromQuickBooks,
              });
              setIsInvoiceDateModalOpen(false);
            },
          },
        ]}
      >
        <Box mt={2}>
          <MiDatePicker
            label="Invoice Date"
            value={invoiceDate}
            onChange={(date) => setInvoiceDate(date)}
            variant={DATEPICKER_VARIANTS.DESKTOP}
            actions={[DATEPICKER_ACTIONS.CANCEL]}
          />

          {quickBooksSyncedInvoices.length > 0 && (
            <Box display="flex" flexDirection="column" gap={1} mt={2}>
              <Typography
                variant="body1"
                fontWeight="bold"
                color="text.primary"
              >
                Sync invoices to QuickBooks
              </Typography>
              <MiAlert
                isOpen={anyInvoiceExcluded}
                description="Not all invoices from the order will be synced to QuickBooks."
                variant="warning"
              />
              <Box className={classes.invoicesSelection} py={2} px={1} gap={4}>
                {quickBooksSyncedInvoices.map((invoice) => (
                  <MiCheckbox
                    key={`exclude-qb-${invoice.id}`}
                    name={invoice.name}
                    label={invoice.name}
                    value={isInvoiceIncludedInQuickBooks(invoice.id)}
                    handleChange={({ target: { checked } }) => {
                      toggleInvoiceExclusion(invoice.id, checked);
                    }}
                    size="medium"
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </MiModal>

      <MiActions actions={isInvoiceMode ? invoicingActions : actions} />
      <MiModal
        open={isConfirmVarianceModalOpen}
        closeModal={() => setIsConfirmVarianceModalOpen(false)}
        width={480}
        titleIcon={mdi.mdiInformationOutline}
        leftIconColor={theme.palette.primary.main}
        title="Confirm Complete with Variance"
        loading={isLoading}
        actions={[
          {
            name: "Cancel",
            variant: BUTTON_VARIANTS.TEXT,
            color: BUTTON_COLORS.BLACK,
            onClick: () => setIsConfirmVarianceModalOpen(false),
          },
          {
            name: "Confirm & Continue",
            variant: BUTTON_VARIANTS.CONTAINED,
            onClick: () => {
              invoicesModule.handlePreBillOrderWithManagerApproval();
              setIsConfirmVarianceModalOpen(false);
            },
          },
        ]}
      >
        <Typography variant="body2" color="text.secondary" mb={2} px={2}>
          You are about to complete the pre-bill process with an outstanding
          variance on an invoice. Confirm you'd like to proceed.
        </Typography>
      </MiModal>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    paddingRight: theme.spacing(3),
    borderTop: `1px solid ${theme.palette.grey.accent}`,
    backgroundColor: theme.palette.background.paper,
    gap: theme.spacing(2),
    height: 72,
    overflow: "hidden",
  },
  invoicesSelection: {
    display: "flex",
    flexDirection: "column",
    border: `1px solid ${theme.palette.grey.A400}`,
    borderRadius: "4px",
  },
}));
